import React, { useState, useEffect, memo } from 'react';
import FullCalendarComponent from './FullCalendarComponent';
import NiceMobileCalendar from './NiceMobileCalendar';
import { Event } from '../types/Event';
import './CalendarSelector.css';

import logger from '../utils/logger';
interface Provider { id: number; name: string; calendar_color: string | null }
interface RoomResource { id: number; name: string; color: string }

interface CalendarSelectorProps {
  events: Event[];
  onEventClick: (event: Event) => void;
  onDateClick: (slotInfo: { start: Date; end: Date }) => void;
  onEventDrop: (eventInfo: { event: Event; start: Date; end: Date }) => void;
  onEventResize: (eventInfo: { event: Event; start: Date; end: Date }) => void;
  providers?: Provider[];
  selectedProviderId?: number | null;
  onProviderChange?: (id: number | null) => void;
  resources?: RoomResource[];
  selectedResourceId?: number | null;
  onResourceChange?: (id: number | null) => void;
}

const CalendarSelector: React.FC<CalendarSelectorProps> = (props) => {
  const { providers = [], selectedProviderId = null, onProviderChange } = props;
  const { resources = [], selectedResourceId = null, onResourceChange } = props;
  const filteredEvents = props.events.filter(e => {
    if (selectedProviderId && e.providerId !== selectedProviderId) return false;
    if (selectedResourceId && !(e.resourceIds || []).includes(selectedResourceId)) return false;
    return true;
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) && window.innerWidth <= 768;
  });

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent;
      const screenWidth = window.innerWidth;
      const touchSupport = 'ontouchstart' in window;
      const maxTouchPoints = navigator.maxTouchPoints || 0;
      
      // Very conservative mobile detection - only detect actual mobile devices
      const userAgentMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const screenWidthMobile = screenWidth <= 768;
      const touchDevice = touchSupport && maxTouchPoints > 0;
      const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
      const isChromeMobile = /CriOS/i.test(userAgent);
      
      // Very conservative mobile detection - require user agent AND screen width
      const isMobileDevice = userAgentMobile && screenWidthMobile;
      
      logger.log('📱 CalendarSelector Mobile Detection:');
      logger.log('User Agent:', userAgent);
      logger.log('Screen Width:', screenWidth);
      logger.log('Touch Support:', touchSupport);
      logger.log('Max Touch Points:', maxTouchPoints);
      logger.log('User Agent Mobile:', userAgentMobile);
      logger.log('Screen Width Mobile:', screenWidthMobile);
      logger.log('Touch Device:', touchDevice);
      logger.log('Is iOS:', isIOS);
      logger.log('Is Chrome Mobile:', isChromeMobile);
      logger.log('Final Is Mobile:', isMobileDevice);
      
      setIsMobile(isMobileDevice);
      
      if (isMobileDevice) {
        logger.log('📱 Mobile detected, using NiceMobileCalendar');
      } else {
        logger.log('🖥️ Desktop detected, using FullCalendar');
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const calProps = { ...props, events: filteredEvents };

  return (
    <div className="calendar-selector">
      {/* Provider filter — only shown when there are multiple providers */}
      {providers.length > 1 && onProviderChange && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 16px 0', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => onProviderChange(null)}
            style={{
              padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--border)',
              background: selectedProviderId === null ? 'var(--accent)' : 'transparent',
              color: selectedProviderId === null ? 'var(--accent-contrast)' : 'var(--ink-muted)',
            }}
          >
            Todos
          </button>
          {providers.map(p => (
            <button
              key={p.id}
              onClick={() => onProviderChange(p.id)}
              style={{
                padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--border)',
                background: selectedProviderId === p.id ? (p.calendar_color || 'var(--accent)') : 'transparent',
                color: selectedProviderId === p.id ? '#fff' : 'var(--ink-muted)',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              {p.calendar_color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.calendar_color, flexShrink: 0 }} />}
              {p.name}
            </button>
          ))}
        </div>
      )}

      {/* Room filter — mirrors the provider filter. Pick a room to see only its
          bookings; combined with provider colors this shows who uses each room. */}
      {resources.length > 0 && onResourceChange && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 16px 0', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => onResourceChange(null)}
            style={{
              padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--border)',
              background: selectedResourceId === null ? 'var(--accent)' : 'transparent',
              color: selectedResourceId === null ? 'var(--accent-contrast)' : 'var(--ink-muted)',
            }}
          >
            Todas as salas
          </button>
          {resources.map(r => (
            <button
              key={r.id}
              onClick={() => onResourceChange(r.id)}
              style={{
                padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--border)',
                background: selectedResourceId === r.id ? (r.color || 'var(--accent)') : 'transparent',
                color: selectedResourceId === r.id ? '#fff' : 'var(--ink-muted)',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 3, background: r.color || 'var(--accent)', flexShrink: 0 }} />
              {r.name}
            </button>
          ))}
        </div>
      )}

      {/* Calendar Component */}
      <div className="calendar-wrapper">
        {isMobile ? (
          <NiceMobileCalendar {...calProps} />
        ) : (
          <FullCalendarComponent {...calProps} />
        )}
      </div>
    </div>
  );
};

export default memo(CalendarSelector);